<template>
  <div class="handled-context-container h-full">
    <!-- Context Selection Interface -->
    <div class="context-selection h-full flex flex-col">
      <!-- Modal Header -->
      <div class="flex items-center justify-between p-4 pb-2 bg-eggplant-50/50 dark:bg-gray-800/30">
        <div class="flex items-center gap-3">
          <SparkleAnim class="h-6 w-6 text-eggplant-500" />
          <h3 class="text-lg font-semibold text-gray-700 dark:text-gray-200">
            Quick Ask<span class="font-normal text-base">
              – Quick answers to one-time questions</span
            >
          </h3>
        </div>
        <div class="flex items-center">
          <button
            @click="$emit('close')"
            class="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full text-gray-500 dark:text-gray-400 transition-colors"
          >
            <XMarkIcon class="h-5 w-5" />
          </button>
        </div>
      </div>

      <!-- Conversation-style content area -->
      <div
        ref="contentRef"
        class="context-content flex-1 p-12 space-y-8 overflow-x-hidden overflow-y-auto scroll-smooth"
      >
        <!-- Welcome message (permanent) -->
        <div class="mb-8 md:w-9/12">
          <div>
            <h4 class="text-xl font-medium text-gray-700 dark:text-gray-200">
              Hello, I'm your Handled AI Assistant.
            </h4>
            <p class="text-base text-gray-700 dark:text-gray-200">
              I have a few questions to ask you before we start.
            </p>
          </div>
        </div>

        <!-- Conversation History -->
        <div
          v-for="(item, index) in conversationHistory"
          :key="`conversation-${index}`"
          class="space-y-6 mb-8"
        >
          <!-- AI Question -->
          <div class="flex md:w-9/12">
            <div class="text-gray-700 dark:text-gray-300">
              <p class="font-medium mb-1">{{ item.step.title }}</p>
              <p v-html="sanitizeHtml(item.step.question)"></p>
            </div>
          </div>

          <!-- User Response (if they've made a choice) -->
          <div v-if="item.userChoice" class="flex justify-end md:w-9/12 md:ml-auto">
            <div
              class="bg-eggplant-50 dark:bg-eggplant-900 text-gray-900 dark:text-gray-100 px-4 py-3 rounded-2xl rounded-br-md"
            >
              <p>{{ item.userChoice.text }}</p>
            </div>
          </div>
        </div>

        <!-- Current Question (if not completed) -->
        <div v-if="!isCompleted" class="space-y-6">
          <!-- AI Question for current step -->
          <div class="flex md:w-9/12">
            <div class="text-gray-700 dark:text-gray-300">
              <p class="font-medium mb-1">{{ currentStep.title }}</p>
              <p v-html="sanitizeHtml(currentStep.question)"></p>
            </div>
          </div>

          <!-- Campaign Picker -->
          <div
            v-if="currentStep.id.includes('campaign-picker')"
            ref="campaignPickerRef"
            class="md:w-9/12"
          >
            <CampaignPicker @select="handleCampaignSelect" @dropdownOpen="handleDropdownOpen" />
          </div>

          <!-- Case Picker -->
          <div v-if="currentStep.id.includes('case-picker')" ref="casePickerRef" class="md:w-9/12">
            <CasePicker
              :campaign-id="
                currentStep.id.includes('same-campaign') ||
                currentStep.id.includes('campaign-selection') ||
                currentStep.id.includes('from-campaign') ||
                currentStep.id.includes('after-campaign')
                  ? selection.campaignId || currentPageContext.id || undefined
                  : undefined
              "
              @select="handleCaseSelect"
              @dropdownOpen="handleDropdownOpen"
            />
          </div>

          <!-- Current Choices -->
          <div
            v-if="
              !currentStep.id.includes('campaign-picker') && !currentStep.id.includes('case-picker')
            "
            class="flex flex-wrap gap-3 md:w-9/12"
          >
            <ContextChoice
              v-for="choice in displayedChoices"
              :key="choice.id"
              :text="choice.text"
              :description="choice.description"
              @select="handleChoiceSelect(choice)"
            />

            <!-- Show more button for large lists -->
            <button
              v-if="hasMoreChoices"
              @click="showMoreChoices"
              class="inline-flex items-center px-4 py-2 text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 border border-dashed border-gray-300 dark:border-gray-600 rounded-full hover:border-gray-400 dark:hover:border-gray-500"
            >
              Show {{ remainingChoicesCount }} more...
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { XMarkIcon } from "@heroicons/vue/24/outline";
import { useQueryClient } from "@tanstack/vue-query";
import { ref, computed, watch, nextTick } from "vue";

import { useContextSelection } from "@/composables/useContextSelection";
import { campaignQueries } from "@/queries/campaigns";
import { sanitizeHtml } from "@/utils/sanitizeHtml";

import CampaignPicker from "./CampaignPicker.vue";
import CasePicker from "./CasePicker.vue";
import ContextChoice from "./ContextChoice.vue";
import SparkleAnim from "./SparkleAnim.vue";

interface ContextSelection {
  campaignId?: string;
  caseId?: string;
  caseName?: string;
  data?: Record<string, unknown>;
  step: string;
  type: "campaign" | "case" | "firm" | null;
}

const emit = defineEmits<{
  close: [];
  startConversation: [selection: ContextSelection];
}>();

// Use the context selection composable (gates are read from auth store)
const { currentChoices, currentPageContext, currentStep, selectChoice, selection } =
  useContextSelection();

// vue-query hooks must be called in setup(), not inside event handlers
const queryClient = useQueryClient();

// Template refs
const contentRef = ref<HTMLElement | null>(null);
const campaignPickerRef = ref<HTMLElement | null>(null);
const casePickerRef = ref<HTMLElement | null>(null);

// Conversation history for chat-like display
const conversationHistory = ref<
  Array<{
    step: { id: string; question: string; title: string };
    userChoice?: { description?: string; id: string; text: string };
  }>
>([]);

// Display state
const maxDisplayedChoices = ref(5);

// Completion state
const isCompleted = ref(false);

// Scroll to bottom function
const scrollToBottom = () => {
  if (contentRef.value) {
    contentRef.value.scrollTo({
      behavior: "smooth",
      top: contentRef.value.scrollHeight,
    });
  }
};

// Handle dropdown open events from picker components
const handleDropdownOpen = (isOpen: boolean) => {
  if (isOpen) {
    nextTick(() => {
      // Find the active picker element
      const campaignPicker = campaignPickerRef.value;
      const casePicker = casePickerRef.value;

      // Try a different approach: scroll to ensure enough space for dropdown
      if (campaignPicker && currentStep.value.id.includes("campaign-picker")) {
        setTimeout(() => {
          // Instead of scrolling picker to top, scroll just enough to show dropdown
          if (contentRef.value) {
            const container = contentRef.value;
            const pickerRect = campaignPicker.getBoundingClientRect();
            const containerRect = container.getBoundingClientRect();

            // Calculate how much space we have below the picker
            const spaceBelow = containerRect.bottom - pickerRect.bottom;
            const dropdownHeight = 250; // Estimated dropdown height
            const paddingBuffer = 100; // Extra space for padding (increased for more breathing room)

            // Only scroll if we don't have enough space
            if (spaceBelow < dropdownHeight + paddingBuffer) {
              const scrollNeeded = dropdownHeight + paddingBuffer - spaceBelow;
              container.scrollTo({
                behavior: "smooth",
                top: container.scrollTop + scrollNeeded,
              });
            }
          }
        }, 100);
      } else if (casePicker && currentStep.value.id.includes("case-picker")) {
        setTimeout(() => {
          // Same logic for case picker
          if (contentRef.value) {
            const container = contentRef.value;
            const pickerRect = casePicker.getBoundingClientRect();
            const containerRect = container.getBoundingClientRect();

            const spaceBelow = containerRect.bottom - pickerRect.bottom;
            const dropdownHeight = 250;
            const paddingBuffer = 100; // Extra space for padding (increased for more breathing room)

            if (spaceBelow < dropdownHeight + paddingBuffer) {
              const scrollNeeded = dropdownHeight + paddingBuffer - spaceBelow;
              container.scrollTo({
                behavior: "smooth",
                top: container.scrollTop + scrollNeeded,
              });
            }
          }
        }, 100);
      }
    });
  }
};

// Watch for changes in conversation history to auto-scroll
watch(
  () => conversationHistory.value.length,
  async () => {
    await nextTick();
    setTimeout(() => {
      scrollToBottom();
    }, 50); // Small delay to ensure content is fully rendered
  }
);

// Watch for changes in current step to auto-scroll
watch(
  () => currentStep.value.id,
  async () => {
    await nextTick();
    setTimeout(() => {
      scrollToBottom();
    }, 100); // Slightly longer delay for step transitions
  }
);

// Watch for changes in displayed choices (e.g., when "Show more" is clicked)
watch(
  () => maxDisplayedChoices.value,
  async () => {
    await nextTick();
    setTimeout(() => {
      scrollToBottom();
    }, 50);
  }
);

// Computed properties for choice display
const displayedChoices = computed(() => {
  if (!currentChoices.value) return [];
  return currentChoices.value.slice(0, maxDisplayedChoices.value);
});

const hasMoreChoices = computed(() => {
  return currentChoices.value.length > maxDisplayedChoices.value;
});

const remainingChoicesCount = computed(() => {
  return Math.max(0, currentChoices.value.length - maxDisplayedChoices.value);
});

// Show more choices
function showMoreChoices() {
  maxDisplayedChoices.value += 10;
}

// Handle choice selection
function handleChoiceSelect(choice: { description?: string; id: string; text: string }) {
  // Add to conversation history
  conversationHistory.value.push({
    step: { ...currentStep.value },
    userChoice: choice,
  });

  const result = selectChoice(choice);

  if (result === "start-conversation") {
    // Complete the flow
    isCompleted.value = true;
    emit("startConversation", selection.value);
  } else {
    // Continue to next step
    maxDisplayedChoices.value = 5;
    // Ensure scroll to bottom after step transition
    nextTick(() => {
      setTimeout(() => {
        scrollToBottom();
      }, 150);
    });
  }
}

// Handle campaign selection from picker
function handleCampaignSelect(campaignId: string | string[]) {
  // HandledContext only supports single selection, so take the first one if array
  const selectedId = Array.isArray(campaignId) ? campaignId[0] : campaignId;
  const stepId = currentStep.value.id;

  // Look up the campaign name from the vue-query cache (filled by useCampaigns
  // elsewhere in the app). Falls back to the id if the cache hasn't been
  // populated yet.
  const cached = queryClient.getQueryData<{ id: string; name: string }[]>(
    campaignQueries.campaignList().queryKey
  );
  const campaign = cached?.find((c) => c.id === selectedId);
  const campaignName = campaign?.name || selectedId;

  // Add to conversation history
  conversationHistory.value.push({
    step: { ...currentStep.value },
    userChoice: {
      id: selectedId,
      text: campaignName,
    },
  });

  // Scroll to bottom after adding to conversation
  nextTick(() => {
    setTimeout(() => {
      scrollToBottom();
    }, 100);
  });

  // Determine next step based on current step
  if (stepId === "campaign-picker-then-case") {
    // Go to case picker after campaign selection (when user wants different campaign then case)
    selection.value.campaignId = selectedId;
    selectChoice({
      id: "continue",
      nextStep: "case-picker-after-campaign",
      text: "Continue",
    });
  } else if (
    stepId === "campaign-picker-from-case" ||
    stepId === "campaign-picker-from-campaign" ||
    stepId === "campaign-picker-for-campaign"
  ) {
    // Ask if they want specific case or overall campaign
    selection.value.campaignId = selectedId;
    selectChoice({
      id: "continue",
      nextStep: "campaign-case-question",
      text: "Continue",
    });
  } else if (stepId === "campaign-picker-for-case") {
    // Go directly to case picker
    selection.value.campaignId = selectedId;
    selectChoice({
      id: "continue",
      nextStep: "case-picker-after-campaign",
      text: "Continue",
    });
  } else {
    // Default: start campaign conversation
    addPickerSelectionToHistory(selectedId, "campaign", campaignName);
  }
}

// Handle case selection from picker
async function handleCaseSelect(caseData: { id: string; name: string; number: string }) {
  const caseId = caseData.id;
  // Use the case data that was already loaded in the picker (no API call needed)
  const caseName = `${caseData.name} (#${caseData.number})`;

  // No API call needed - we already have the case name from the picker

  // Ensure we have the campaign ID for the case
  if (!selection.value.campaignId && currentPageContext.value.type === "campaign") {
    selection.value.campaignId = currentPageContext.value.id;
  }

  addPickerSelectionToHistory(caseId, "case", caseName);
}

// Helper function for picker selections
function addPickerSelectionToHistory(id: string, type: "campaign" | "case", displayName?: string) {
  conversationHistory.value.push({
    step: { ...currentStep.value },
    userChoice: {
      id: id,
      text: displayName || id,
    },
  });

  // Scroll to bottom after adding to conversation
  nextTick(() => {
    setTimeout(() => {
      scrollToBottom();
    }, 100);
  });

  selection.value.type = type;
  if (type === "campaign") {
    selection.value.campaignId = id;
  } else {
    selection.value.caseId = id;
    selection.value.caseName = displayName;
    // For case selections, preserve the campaignId if it exists
    // This handles the case where we selected a campaign first, then a case
  }

  isCompleted.value = true;
  emit("startConversation", selection.value);
}
</script>

<style scoped>
.custom-scrollbar {
  scrollbar-width: thin;
  scrollbar-color: #d1d5db transparent;
}

.custom-scrollbar::-webkit-scrollbar {
  width: 8px;
}

.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}

.custom-scrollbar::-webkit-scrollbar-thumb {
  background-color: #d1d5db;
  border-radius: 20px;
}

.dark .custom-scrollbar::-webkit-scrollbar-thumb {
  background-color: #4b5563;
}

.context-selection {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.context-content {
  flex: 1;
  min-height: 0;
}
</style>
